import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { Dialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { betragText, eurC, inEurZahl } from '../lib/money';
import { useToast } from '../lib/toast';
import {
  abgerechnet,
  abschlaegeZu,
  angeboteUrl,
  angebotInfo,
  angebotMenue,
  angebotPdfUrl,
  angebotRechnungUrl,
  angebotSchritt,
  angebotUrl,
  ANZEIGE_NAME,
  rechnungAusAngebot,
  type AngebotAktion,
  type AngebotEintrag,
  type AngebotKontext,
  type AngebotRechnung,
  type AngebotStatus,
  type AngebotZeile,
} from './AngebotDaten';
import { AngebotForm } from './AngebotForm';
import { auftragAusAngebotUrl, auftragPdfUrl, neuerAuftrag } from './AuftragDaten';
import { Entwuerfe } from './Entwuerfe';
import { deProzent, parseProzent } from './formular';
import { PdfDialog } from './PdfDialog';
import { darf, MitRecht, NurAnsehen } from './tarifHinweis';
import type { Overview } from './types';
import { urlId } from './urlId';
import { VersandDialog, versandText, type VersandBeleg } from './VersandDialog';
import { ZeilenMenue } from './ZeilenMenue';
import './angebote.css';

// Angebote (/rechnung/angebote): Liste mit Status, Suche und Filter, je Angebot eine Zeile wie bei den Rechnungen
// (AngebotDaten.ts: graue Zeile, ein Knopf, Menü „···“); /rechnung/angebote/neu: neues Angebot (AngebotForm.tsx, auch
// zum Kopieren und für Entwürfe). ?angebot=<id> (Link in der E-Mail und der Glocke nach der Antwort des Kunden): dieses
// Angebot zeigen und kurz hervorheben.
export function Angebote({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  return (
    <Routes>
      <Route index element={<Liste data={data} />} />
      <Route
        path="neu"
        element={
          <MitRecht tarif={data.tarif} bereich="angebote" zu="/rechnung/angebote">
            <AngebotForm data={data} reload={reload} />
          </MitRecht>
        }
      />
      <Route path="*" element={<Navigate to="/rechnung/angebote" replace />} />
    </Routes>
  );
}

type View = { url: string; title: string; beleg?: VersandBeleg; hinweis?: string | null };
const beleg = (g: AngebotZeile): VersandBeleg => ({ art: 'angebot', id: g.id, titel: `Angebot ${g.number}` });
// „Per E-Mail“ im Fenster der PDF nicht bei abgelehnten Angeboten; wann und an wen verschickt steht dort immer (auch
// ohne das Recht und bei abgelehnten, wo das Fenster „Per E-Mail“ fehlt)
const viewOf = (g: AngebotZeile): View => ({
  url: angebotPdfUrl(g.id),
  title: `Angebot ${g.number}`,
  beleg: g.status === 'abgelehnt' ? undefined : beleg(g),
  hinweis: versandText(g.versand),
});

type Filter = 'alle' | 'offen' | 'abgelaufen' | 'angenommen' | 'abgelehnt';
const FILTERS: [Filter, string][] = [
  ['alle', 'Alle'],
  ['offen', 'Offen'],
  ['abgelaufen', 'Abgelaufen'],
  ['angenommen', 'Angenommen'],
  ['abgelehnt', 'Abgelehnt'],
];
// Filter aus der Adresse, z. B. /rechnung/angebote?filter=abgelaufen
const filterOf = (v: string | null): Filter => FILTERS.find(([f]) => f === v)?.[0] ?? 'alle';
// „Offen“: noch ohne Antwort des Kunden (auch die abgelaufenen)
const matches = (g: AngebotZeile, f: Filter) => (f === 'alle' ? true : f === 'offen' ? g.status === 'offen' : g.anzeige === f);
// Suche: Kunde, Nummer (auch nur „14“), Objekt, Bestellnummer aus der Antwort online oder Betrag („1.190“, „1190,00“,
// „1.190,00 USD“)
function found(g: AngebotZeile, q: string) {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  const digits = t.replace(/[.\s€]|\b[a-z]{3}\b/g, '');
  return (
    g.customer.toLowerCase().includes(t) ||
    g.number.toLowerCase().includes(t) ||
    (g.site ?? '').toLowerCase().includes(t) ||
    (g.online?.referenz ?? '').toLowerCase().includes(t) ||
    (digits !== '' && betragText(g.betrag).replace(/[.\s]/g, '').includes(digits))
  );
}
// Summe in Euro (Angebote in anderer Währung zum Kurs des Angebots); fremd: eins davon ist nicht in Euro
const sumOf = (list: AngebotZeile[]) => list.reduce((s, g) => s + inEurZahl(g.betrag, g.kurs), 0);
const umgerechnet = (list: AngebotZeile[]) => (list.some((g) => g.waehrung !== 'EUR') ? ' · umgerechnet zum Kurs der Angebote' : '');
const anzahl = (n: number) => (n === 1 ? '1 Angebot' : `${n} Angebote`);

// Alle Angebote der Firma, neueste zuerst: oben offen, abgelaufen, angenommen; darunter Filter und Suche
function Liste({ data }: { data: Overview }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState<AngebotZeile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [send, setSend] = useState<VersandBeleg | null>(null);
  const [abschlag, setAbschlag] = useState<AngebotZeile | null>(null);
  // „Auftrag bestätigen“: Rückfrage vor der Auftragsbestätigung
  const [bestaetigen, setBestaetigen] = useState<AngebotZeile | null>(null);
  // „Antwort des Kunden“: Bestellnummer und Nachricht bzw. Grund aus der Antwort online
  const [antwort, setAntwort] = useState<AngebotZeile | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Billtoro: Rechte des Logins (nur Anzeige, der Server prüft); Rechnung aus dem Angebot braucht „Rechnungen“
  const bieten = darf(data.tarif, 'angebote');
  const rechnen = darf(data.tarif, 'rechnungen');
  const ctx: AngebotKontext = { bieten, rechnen };
  const [params] = useSearchParams();
  const urlFilter = params.get('filter');
  const [filter, setFilter] = useState<Filter>(() => filterOf(urlFilter));
  useEffect(() => setFilter(filterOf(urlFilter)), [urlFilter]);
  // Suche aus der Adresse, z. B. ?suche=RE-2026-0031 (Links im Vorgang bei den Aufträgen)
  const urlSuche = params.get('suche');
  const [q, setQ] = useState(urlSuche ?? '');
  useEffect(() => {
    if (urlSuche !== null) setQ(urlSuche);
  }, [urlSuche]);
  // Angebot aus der Adresse: einmal je Adresse, sobald die Liste da ist (gehört es nicht dazu: nichts tun)
  const urlAngebot = urlId(params.get('angebot'));
  const [hervor, setHervor] = useState<string | null>(null);
  const gesprungen = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList((await api.get<{ angebote: AngebotZeile[] }>(angeboteUrl(data.firm.id))).angebote);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [data.firm.id]);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!urlAngebot || gesprungen.current === urlAngebot || !list?.some((g) => g.id === urlAngebot)) return;
    gesprungen.current = urlAngebot;
    setFilter('alle');
    setHervor(urlAngebot);
  }, [urlAngebot, list]);
  useEffect(() => {
    if (!hervor) return;
    document.getElementById(`ag-${hervor}`)?.scrollIntoView({ block: 'center' });
    const t = setTimeout(() => setHervor(null), 4000);
    return () => clearTimeout(t);
  }, [hervor]);

  // Gerade geschrieben: gleich die PDF zeigen, zum Herunterladen und Verschicken
  useEffect(() => {
    const st = location.state as { show?: string; number?: string } | null;
    if (st?.show) {
      const title = `Angebot ${st.number ?? ''}`;
      setView({ url: angebotPdfUrl(st.show), title, beleg: { art: 'angebot', id: st.show, titel: title } });
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  // Auftragsbestätigung mit einem Klick (Kunde, Leistungen und Preise aus dem Angebot), dann gleich ihre PDF zum Senden
  async function auftragBestaetigen(g: AngebotZeile) {
    setBusy(g.id);
    try {
      const res = await api.post<{ id: string; number: string }>(auftragAusAngebotUrl(g.id), {});
      setBestaetigen(null);
      toast(`Auftragsbestätigung ${res.number} geschrieben.`);
      void load();
      const title = `Auftragsbestätigung ${res.number}`;
      setView({ url: auftragPdfUrl('auftrag', res.id), title, beleg: { art: 'auftrag', id: res.id, titel: title } });
    } catch (err) {
      setBestaetigen(null);
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function setStatus(g: AngebotZeile, status: AngebotStatus) {
    setBusy(g.id);
    try {
      const neu = await api.put<AngebotZeile>(`${angebotUrl(g.id)}/status`, { status });
      setList((l) => l && l.map((x) => (x.id === neu.id ? neu : x)));
      toast(status === 'offen' ? `Angebot ${g.number} wieder offen` : `Angebot ${g.number} ${status}`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  // Knopf oder Eintrag im Menü: Fenster öffnen bzw. Status setzen (Links rendern knopf und eintrag selbst)
  function tun(a: Exclude<AngebotAktion, { art: 'link' }>, g: AngebotZeile) {
    switch (a.art) {
      case 'mail':
        return setSend(beleg(g));
      case 'auftrag':
        return setBestaetigen(g);
      case 'abschlag':
        return setAbschlag(g);
      case 'antwort':
        return setAntwort(g);
      case 'status':
        return void setStatus(g, a.status);
    }
  }
  // Status und Auftragsbestätigung nicht doppelt, solange der Server noch arbeitet
  const wartetAuf = (a: AngebotAktion, g: AngebotZeile) => busy === g.id && (a.art === 'status' || a.art === 'auftrag');

  // Der eine Knopf der Zeile (.rt-next)
  function knopf(a: AngebotAktion, g: AngebotZeile) {
    if (a.art === 'link')
      return (
        <Link to={a.ziel} className="btn small">
          {a.text}
        </Link>
      );
    return (
      <button type="button" className="btn small" disabled={wartetAuf(a, g)} aria-label={a.art === 'status' ? a.label : undefined} onClick={() => tun(a, g)}>
        {a.text}
      </button>
    );
  }

  // Eintrag im Menü „···“ der Zeile
  function eintrag(e: AngebotEintrag, g: AngebotZeile, n: number) {
    if (e.art === 'trenner') return <div key={`trenner-${n}`} role="separator" />;
    if (e.art === 'link')
      return (
        <Link key={e.text} role="menuitem" to={e.ziel}>
          {e.text}
        </Link>
      );
    return (
      <button
        key={e.text}
        type="button"
        role="menuitem"
        disabled={wartetAuf(e, g)}
        aria-label={e.art === 'status' ? e.label : undefined}
        onClick={() => tun(e, g)}
      >
        {e.text}
      </button>
    );
  }

  const head = (
    <div className="rt-head">
      <div>
        <h1>
          Angebote <NurAnsehen tarif={data.tarif} bereich="angebote" />
        </h1>
        <p className="muted">{list === null ? '' : list.length ? anzahl(list.length) : 'Noch keine Angebote'}</p>
      </div>
      {bieten && (
        <div className="rt-head-act">
          <Link to="/rechnung/angebote/neu" className="btn primary">
            + Neues Angebot
          </Link>
        </div>
      )}
    </div>
  );
  if (list === null) {
    return (
      <>
        {head}
        {error ? <div className="notice bad">{error}</div> : <p className="muted">Lädt …</p>}
      </>
    );
  }

  const offen = list.filter((g) => g.anzeige === 'offen');
  const abgelaufen = list.filter((g) => g.anzeige === 'abgelaufen');
  const angenommen = list.filter((g) => g.status === 'angenommen');
  const ohneRechnung = angenommen.filter((g) => !abgerechnet(g));
  const shown = list.filter((g) => matches(g, filter) && found(g, q));
  const kpi = (f: Filter) => () => setFilter(filter === f ? 'alle' : f);
  const summen = { offen: eurC(sumOf(offen)), abgelaufen: eurC(sumOf(abgelaufen)), angenommen: eurC(sumOf(angenommen)) };
  // ab 100.000,00 € (zwölf Zeichen) passen drei Kacheln am Handy nicht mehr in eine Reihe: dann 2 + 1 (angebote.css)
  const breit = Object.values(summen).some((v) => v.length > 11);

  return (
    <>
      {head}
      <Entwuerfe firmId={data.firm.id} art="angebot" today={data.today} weiter={(id) => `/rechnung/angebote/neu?entwurf=${id}`} aendern={bieten} />

      {list.length > 0 && (
        <>
          <div className={`rt-kpis re-kpis${breit ? ' ag-kpis-breit' : ''}`}>
            <button type="button" className={`rt-kpi${filter === 'offen' ? ' on' : ''}`} onClick={kpi('offen')}>
              <span className="rt-kpi-l">Offen</span>
              <span className="rt-kpi-v num">{summen.offen}</span>
              <span className="rt-kpi-s">
                {anzahl(offen.length)}
                {umgerechnet(offen)}
              </span>
            </button>
            <button
              type="button"
              className={`rt-kpi${abgelaufen.length ? ' bad' : ''}${filter === 'abgelaufen' ? ' on' : ''}`}
              onClick={kpi('abgelaufen')}
            >
              <span className="rt-kpi-l">Abgelaufen</span>
              <span className="rt-kpi-v num">{summen.abgelaufen}</span>
              <span className="rt-kpi-s">
                {abgelaufen.length ? `${anzahl(abgelaufen.length)} · nachfassen` : 'keins abgelaufen'}
                {umgerechnet(abgelaufen)}
              </span>
            </button>
            <button type="button" className={`rt-kpi ok${filter === 'angenommen' ? ' on' : ''}`} onClick={kpi('angenommen')}>
              <span className="rt-kpi-l">Angenommen</span>
              <span className="rt-kpi-v num">{summen.angenommen}</span>
              <span className="rt-kpi-s">
                {anzahl(angenommen.length)}
                {ohneRechnung.length ? ` · ${ohneRechnung.length} ohne Rechnung` : ''}
                {umgerechnet(angenommen)}
              </span>
            </button>
          </div>
          <div className="rt-tools ag-tools">
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Kunde, Nummer, Objekt oder Betrag"
              title="Suchen: Kunde, Nummer, Objekt, Bestellnummer oder Betrag"
              aria-label="Angebote suchen"
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="rt-chips" role="group" aria-label="Filter">
              {FILTERS.map(([f, label]) => (
                <button key={f} type="button" className={`rt-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {list.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Schreiben Sie Ihr erstes Angebot. Nimmt der Kunde es an, wird daraus mit einem Klick die Rechnung.</p>
          {bieten && (
            <Link to="/rechnung/angebote/neu" className="btn primary">
              Erstes Angebot schreiben
            </Link>
          )}
        </div>
      ) : shown.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Kein Angebot passt zu dieser Auswahl.</p>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setFilter('alle');
              setQ('');
            }}
          >
            Alle zeigen
          </button>
        </div>
      ) : (
        <ul className="rt-list re-list ag-list">
          {shown.map((g) => {
            const ab = abschlaegeZu(g, data.items).map((i) => i.number);
            const schritt = angebotSchritt(g, ab, ctx);
            const eintraege = angebotMenue(g, ab, ctx, schritt);
            const info = angebotInfo(g, ab, data.today);
            return (
              <li key={g.id} id={`ag-${g.id}`} className={`rt-row${hervor === g.id ? ' on' : ''}`}>
                <button type="button" className="rt-row-main" onClick={() => setView(viewOf(g))}>
                  <span className="rt-no num">{g.number}</span>
                  <span className="re-kunde">
                    <span className="rt-cust" title={g.customer}>
                      {g.customer}
                    </span>
                    {info && (
                      <span className={`rt-row-info${info.warn ? ' warn' : ''}`}>
                        <bdi>{info.teile.join(' · ')}</bdi>
                      </span>
                    )}
                  </span>
                  <span className="rt-date muted">{fmtDate(g.date)}</span>
                  <span className="rt-amt num">{eurC(g.betrag, g.waehrung)}</span>
                  <Status g={g} />
                </button>
                {(schritt || eintraege.length > 0) && (
                  <div className="re-act">
                    {schritt && <div className="rt-next">{knopf(schritt, g)}</div>}
                    {eintraege.length > 0 && <ZeilenMenue nummer={g.number}>{eintraege.map((e, n) => eintrag(e, g, n))}</ZeilenMenue>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <PdfDialog
        url={view?.url ?? null}
        title={view?.title ?? ''}
        download={view ? `${view.url}?download=1` : undefined}
        onClose={() => setView(null)}
        hinweis={view?.hinweis}
        onMail={
          view?.beleg && bieten
            ? () => {
                setSend(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
      />
      <VersandDialog beleg={send} tarif={data.tarif} onClose={() => setSend(null)} onSent={load} />
      <AbschlagDialog g={abschlag} onClose={() => setAbschlag(null)} />
      <AntwortDialog g={antwort} onClose={() => setAntwort(null)} />
      {/* Titel ohne Nummer (sie bräche am Bindestrich um), die Nummer steht im ersten Satz */}
      <Dialog open={!!bestaetigen} onClose={() => setBestaetigen(null)} title="Auftragsbestätigung schreiben?" className="ag-frage">
        {bestaetigen && (
          <>
            <p className="muted">
              Aus Angebot <span className="num ag-nr">{bestaetigen.number}</span>: Kunde, Leistungen und Preise ({eurC(bestaetigen.betrag, bestaetigen.waehrung)}) werden
              übernommen. Die Auftragsbestätigung bekommt die nächste Nummer und lässt sich danach nicht mehr ändern.
              {bestaetigen.anzeige === 'abgelaufen' ? ` Das Angebot galt bis ${fmtDate(bestaetigen.validUntil)}.` : ''}
            </p>
            <div className="dlg-actions">
              <button type="button" className="btn" onClick={() => setBestaetigen(null)}>
                Abbrechen
              </button>
              <Link to={neuerAuftrag({ angebot: bestaetigen.id })} className="btn">
                Erst bearbeiten
              </Link>
              <button
                type="button"
                className="btn primary"
                disabled={busy === bestaetigen.id}
                autoFocus
                onClick={() => void auftragBestaetigen(bestaetigen)}
              >
                Ja, schreiben
              </button>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
}

// Chip in den Farben jeder Seite: offen gelb, abgelaufen rot (nachfassen), angenommen grün, abgelehnt grau
function Status({ g }: { g: AngebotZeile }) {
  if (abgerechnet(g)) return <span className="badge b-ok">Abgerechnet</span>;
  const cls = { offen: 'b-warn', abgelaufen: 'b-bad', angenommen: 'b-ok', abgelehnt: 'b-none' }[g.anzeige];
  return <span className={`badge ${cls}`}>{ANZEIGE_NAME[g.anzeige]}</span>;
}

// Antwort des Kunden über den Link aus der E-Mail: wer, wann, Bestellnummer und die ganze Nachricht bzw. der Grund mit
// seinen Zeilen (in der Liste steht nur der Anfang)
function AntwortDialog({ g, onClose }: { g: AngebotZeile | null; onClose(): void }) {
  const o = g?.online;
  return (
    <Dialog
      open={!!o}
      onClose={onClose}
      title={
        <>
          Antwort zu Angebot <span className="num ag-nr">{g?.number}</span>
        </>
      }
    >
      {o && (
        <>
          <p className="ag-antwort">
            {o.art === 'angenommen'
              ? `Online angenommen${o.name ? ` von ${o.name}` : ''} am ${fmtDate(o.am)}`
              : `Online abgelehnt am ${fmtDate(o.am)}${o.name ? ` von ${o.name}` : ''}`}
            {o.referenz && (
              <>
                <br />
                Bestellnummer {o.referenz}
              </>
            )}
          </p>
          {o.nachricht && (
            <div className="ag-nachricht">
              <b>{o.art === 'angenommen' ? 'Nachricht' : 'Grund'}</b>
              {o.nachricht}
            </div>
          )}
          <div className="dlg-actions">
            <button type="button" className="btn" autoFocus onClick={onClose}>
              Schließen
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

// Abschlagsrechnung aus dem Angebot: Anteil in Prozent (je Steuersatz eine Position), bisherige Abschläge zum Vergleich.
// Weiter geht es im Rechnungsformular, vorbefüllt.
function AbschlagDialog({ g, onClose }: { g: AngebotZeile | null; onClose(): void }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState<AngebotRechnung | null>(null);
  const [prozent, setProzent] = useState('30');
  const [error, setError] = useState<string | null>(null);
  // schon selbst eingetippt: der Vorschlag nach dem Laden überschreibt es nicht
  const getippt = useRef(false);

  useEffect(() => {
    if (!g) return;
    setInfo(null);
    setError(null);
    setProzent('30');
    getippt.current = false;
    let live = true;
    api
      .get<AngebotRechnung>(angebotRechnungUrl(g.id))
      .then((r) => {
        if (!live) return;
        setInfo(r);
        // Vorschlag: 30 %, höchstens der Rest
        const rest = Math.round((100 - r.prozentBisher) * 100) / 100;
        if (rest > 0 && !getippt.current) setProzent(deProzent(Math.min(30, rest)));
      })
      .catch((err) => live && setError(errorText(err)));
    return () => {
      live = false;
    };
  }, [g]);

  function weiter(e: FormEvent) {
    e.preventDefault();
    if (!g) return;
    const p = parseProzent(prozent);
    if (typeof p !== 'number' || p <= 0 || p > 100) {
      setError('Bitte den Abschlag in Prozent angeben (über 0 bis 100, z. B. 30).');
      return;
    }
    navigate(rechnungAusAngebot(g.id, 'abschlag', p));
  }

  return (
    <Dialog open={!!g} onClose={onClose} title={`Abschlagsrechnung zu ${g?.number ?? ''}`}>
      {g && (
        <form onSubmit={weiter} noValidate>
          <p className="muted">
            Angebot über {eurC(g.betrag, g.waehrung)} ({eurC(g.netto, g.waehrung)} netto). Die Abschlagsrechnung bekommt je Steuersatz eine Position über diesen
            Anteil; im Formular können Sie alles prüfen.
          </p>
          {info && info.bisher.length > 0 && (
            <div className="ag-bisher">
              <b>Bisher abgerechnet: {deProzent(info.prozentBisher)}&nbsp;%</b>
              <ul>
                {info.bisher.map((b) => (
                  <li key={b.id}>
                    <span className="num">{b.number}</span> vom {fmtDate(b.invoiceDate)}: {deProzent(b.prozent)}&nbsp;% · {eurC(b.betrag, g.waehrung)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <label className="field ag-prozent">
            <span>Abschlag in Prozent</span>
            <input
              value={prozent}
              inputMode="decimal"
              autoFocus
              onChange={(e) => {
                getippt.current = true;
                setProzent(e.target.value);
              }}
            />
          </label>
          {error && <div className="notice bad">{error}</div>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn primary">
              Weiter zur Rechnung
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
